import { cn, formatDateISO, getStatusTextColor } from "@/lib/utils";
import { Cloud } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import {
  ExpandableMessageCard,
  SimpleMessageCard,
  ToolError,
  isBootsmanResult,
} from "./shared";

type GetDeploymentResult = {
  deployment?: {
    id: string;
    status: "pending" | "executing" | "complete" | "cancelled" | "error";
    versionId: string;
    specId: string;
    createdAt: string | Date;
    updatedAt: string | Date;
    steps?: Array<{
      name: string;
      status: "pending" | "processing" | "completed";
      hasError: boolean;
    }>;
  };
  errorMessage?: string;
};

interface DeploymentGetProps {
  messageId: string;
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

export function DeploymentGet({ part }: DeploymentGetProps) {
  const isError = part.status === "error";
  const deploymentId = part.args?.deploymentId as string | undefined;

  // Extract deployment result if available
  const result = (() => {
    if (
      part.status === "complete" &&
      part.result &&
      isBootsmanResult(part.result) &&
      part.result.success
    ) {
      return part.result.data as GetDeploymentResult;
    }
    return null;
  })();

  const deployment = result?.deployment;
  const hasDeployment = deployment && !result?.errorMessage;

  const getStatusText = () => {
    if (part.status === "pending") {
      return deploymentId
        ? "Getting Deploy Info..."
        : "Getting Latest Deploy Info...";
    }

    if (isError) {
      return "Failed to Fetch Deploy Info";
    }

    if (result?.errorMessage) {
      return result.errorMessage;
    }

    if (hasDeployment) {
      return "Fetched Deploy Info";
    }

    return "Deploy Info Retrieved";
  };

  // Deployment status color mapping using semantic colors
  const deploymentStatusColors = {
    complete: "text-success",
    error: "text-destructive",
    cancelled: "text-destructive",
    executing: "text-primary",
    failed: "text-destructive",
  };

  const getStatusColor = (status: string) => {
    return getStatusTextColor(status, deploymentStatusColors);
  };

  // Show simple card if successful and no detailed info to display
  if (hasDeployment && (!deployment.steps || deployment.steps.length === 0)) {
    return (
      <SimpleMessageCard
        status={part.status}
        icon={Cloud}
        title={
          <div className="flex items-center gap-2">
            {getStatusText()}
            <span className={cn("text-sm", getStatusColor(deployment.status))}>
              ({deployment.status})
            </span>
          </div>
        }
      />
    );
  }

  // Show expandable card for detailed deployment info or errors
  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={Cloud}
      title={getStatusText()}
      accordionValue="deployment-get"
    >
      {/* Show deployment details */}
      {hasDeployment && (
        <div className="space-y-2 text-sm">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-sm">ID:</span>
              <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">
                {deployment.id}
              </code>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-sm">Status:</span>
              <span
                className={cn("text-sm", getStatusColor(deployment.status))}
              >
                {deployment.status}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-sm">Created:</span>
              <span className="text-xs font-mono">
                {formatDateISO(deployment.createdAt)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-sm">Updated:</span>
              <span className="text-xs font-mono">
                {formatDateISO(deployment.updatedAt)}
              </span>
            </div>
          </div>

          {/* Show deployment steps if available */}
          {deployment.steps && deployment.steps.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-muted-foreground text-sm mb-2">
                Step Snapshot:
              </div>
              <div className="space-y-1.5">
                {deployment.steps.map((step, index) => (
                  <div
                    key={`${step.name}-${index}`}
                    className="flex items-center gap-2 text-sm"
                  >
                    <div
                      className={cn(
                        "w-1.5 h-1.5 rounded-full flex-shrink-0",
                        step.hasError
                          ? "bg-red-500"
                          : step.status === "completed"
                            ? "bg-green-500"
                            : step.status === "processing"
                              ? "bg-blue-500"
                              : "bg-muted-foreground/40",
                      )}
                    />
                    <span className="flex-1">{step.name}</span>
                    <span className="text-muted-foreground text-xs">
                      {step.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Show tool execution error */}
      {isError && part.result ? <ToolError result={part.result} /> : null}
    </ExpandableMessageCard>
  );
}
